import { useEffect, useRef, useState, useCallback } from "react";
import { chatService } from "../api/chat";

// Derive WS base from the API base URL so local dev uses ws://127.0.0.1:8000
const API_BASE = (import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8000/api/v1").trim();
const WS_BASE_URL = API_BASE
    .replace(/\/api\/v\d+\/?$/, "")  // strip /api/v1 suffix
    .replace(/^http/, "ws");           // http → ws,  https → wss

/**
 * Manages a WebSocket connection to a specific chat room.
 * Loads message history from Firestore (via REST) on room open.
 * Handles WhatsApp-style message status ticks (sent → delivered → read).
 * Support for message actions: Reply, Delete, Pin, Star.
 */
const useChatWebSocket = (roomId, userId) => {
    const [messages, setMessages] = useState([]);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [replyingTo, setReplyingTo] = useState(null); // The message object we are replying to
    const wsRef = useRef(null);

    // Load message history from Firestore (via REST) when room changes
    useEffect(() => {
        if (!roomId || !userId) {
            setMessages([]);
            return;
        }

        setIsLoadingHistory(true);
        setMessages([]);
        setReplyingTo(null);

        chatService.getMessageHistory(roomId)
            .then((data) => {
                const history = (data?.messages || []).map((m) => ({
                    id: m.id,
                    content: m.message,
                    role: String(m.user_id) === String(userId) ? "me" : "other",
                    timestamp: m.timestamp,
                    status: m.status || "sent",
                    reply_to: m.reply_to,
                    is_pinned: m.is_pinned || false,
                    is_starred: m.is_starred || false,
                    is_deleted: m.is_deleted || false,
                }));
                setMessages(history);
            })
            .catch((err) => {
                console.warn("Could not load message history:", err);
            })
            .finally(() => {
                setIsLoadingHistory(false);
            });
    }, [roomId, userId]);

    // WebSocket connection
    useEffect(() => {
        if (!roomId || !userId) return;

        const token = localStorage.getItem("authToken");
        const url = `${WS_BASE_URL}/ws/chat/${roomId}/?token=${token}`;

        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
            setIsConnected(true);
            // Tell backend to mark all unread messages in this room as read
            ws.send(JSON.stringify({
                type: "mark_read",
                user_id: userId,
            }));
        };

        ws.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                if (data.type === "status_update") {
                    const ids = new Set(data.message_ids || []);
                    setMessages((prev) =>
                        prev.map((m) =>
                            ids.has(m.id) ? { ...m, status: data.status } : m
                        )
                    );
                    return;
                }

                if (data.type === "message_deleted") {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === data.message_id
                                ? { ...m, is_deleted: true, content: data.message || "This message was deleted" }
                                : m
                        )
                    );
                    return;
                }

                if (data.type === "metadata_update") {
                    setMessages((prev) =>
                        prev.map((m) =>
                            m.id === data.message_id
                                ? { ...m, ...data.metadata }
                                : m
                        )
                    );
                    return;
                }

                if (data.type === "message" || data.message) {
                    setMessages((prev) => {
                        if (data.client_id) {
                            const exists = prev.some(m => m.id === data.client_id);
                            if (exists) {
                                return prev.map(m =>
                                    m.id === data.client_id
                                        ? {
                                            ...m,
                                            id: data.message_id,
                                            timestamp: data.timestamp,
                                            status: data.status,
                                            reply_to: data.reply_to
                                        }
                                        : m
                                );
                            }
                        }

                        return [
                            ...prev,
                            {
                                id: data.message_id,
                                content: data.message,
                                role: String(data.user_id) === String(userId) ? "me" : "other",
                                timestamp: data.timestamp,
                                status: data.status || "sent",
                                reply_to: data.reply_to,
                                is_pinned: data.is_pinned || false,
                                is_starred: data.is_starred || false,
                                is_deleted: data.is_deleted || false,
                            },
                        ];
                    });
                }
            } catch (err) {
                console.error("WS message parse error", err);
            }
        };

        ws.onclose = () => {
            setIsConnected(false);
        };

        return () => {
            ws.close();
        };
    }, [roomId, userId]);

    const sendMessage = useCallback(
        (text) => {
            const ws = wsRef.current;
            if (!ws || !userId) return;

            const tempId = `temp_${Date.now()}`;
            const replyToId = replyingTo?.id || null;

            const doSend = () => {
                ws.send(JSON.stringify({
                    type: "message",
                    message: text,
                    user_id: userId,
                    client_id: tempId,
                    reply_to: replyToId,
                }));
                // Optimistic UI update
                setMessages((prev) => [
                    ...prev,
                    {
                        id: tempId,
                        content: text,
                        role: "me",
                        timestamp: new Date().toISOString(),
                        status: "sent",
                        reply_to: replyToId,
                        is_deleted: false,
                        is_pinned: false,
                        is_starred: false,
                    },
                ]);
                setReplyingTo(null); // Clear reply state after sending
            };

            if (ws.readyState === WebSocket.OPEN) {
                doSend();
            } else if (ws.readyState === WebSocket.CONNECTING) {
                ws.addEventListener("open", doSend, { once: true });
            }
        },
        [userId, replyingTo]
    );

    const deleteMessage = useCallback((messageId) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({
            type: "delete_message",
            message_id: messageId
        }));
    }, []);

    const togglePinMessage = useCallback((messageId, isPinned) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({
            type: "pin_message",
            message_id: messageId,
            is_pinned: !isPinned
        }));
    }, []);

    const toggleStarMessage = useCallback((messageId, isStarred) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) return;
        ws.send(JSON.stringify({
            type: "star_message",
            message_id: messageId,
            is_starred: !isStarred
        }));
    }, []);

    return {
        messages,
        sendMessage,
        deleteMessage,
        togglePinMessage,
        toggleStarMessage,
        replyingTo,
        setReplyingTo,
        isConnected,
        isLoadingHistory
    };
};

export default useChatWebSocket;
